import { useEffect, useState, useMemo } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import CoinIcon from '../components/CoinIcon';
import TradeDetailPanel from '../components/TradeDetailPanel';
import { HL_WALLET, HL_URL } from '../hyperliquid';
import { tradeResult } from '../tradePnl';
import './Alerts.css';

function formatDateTime(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatPrice(price) {
  if (price == null || price === 0) return '—';
  if (price >= 10000) return `$${Number(price).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (price >= 1000)  return `$${Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 1)     return `$${Number(price).toFixed(4)}`;
  return `$${Number(price).toPrecision(4)}`;
}

function toDateInput(date) { return date.toISOString().slice(0, 10); }

function tsMs(s) {
  return s.timestamp?.toDate ? s.timestamp.toDate().getTime()
       : s.timestamp ? new Date(s.timestamp).getTime() : 0;
}

function statusInfo(signal) {
  if (signal.status === 'executed')          return { label: 'Executed',          cls: 'executed' };
  if (signal.status === 'duplicate_skipped') return { label: 'Duplicate Skipped', cls: 'duplicate' };
  if (signal.status === 'error' || signal.status === 'failed') return { label: 'Failed', cls: 'failed' };
  return { label: signal.status ?? 'Unknown', cls: 'unknown' };
}

function csvEscape(v) {
  return `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
}

const SORT_KEYS = {
  id:       s => parseInt((s.tradeId || '').replace('#', ''), 10) || 0,
  status:   s => s.status || '',
  side:     s => s.direction || '',
  symbol:   s => s.symbol || '',
  strategy: s => s.strategy || s.source || '',
  entry:    s => s.price || 0,
  sl:       s => s.sl_price || 0,
  tp:       s => s.tp_price || 0,
  size:     s => s.size || 0,
  pnl:      s => s._pnl ?? -Infinity,
  time:     s => tsMs(s),
};

function Alerts() {
  const [signals,        setSignals]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [expandedIds,    setExpandedIds]    = useState(new Set());
  const [allFills,       setAllFills]       = useState([]);
  const [sideFilter,     setSideFilter]     = useState('ALL');
  const [symbolFilter,   setSymbolFilter]   = useState('ALL');
  const [strategyFilter, setStrategyFilter] = useState('ALL');
  const [dateFrom,       setDateFrom]       = useState('');
  const [dateTo,         setDateTo]         = useState('');
  const [sortCol,        setSortCol]        = useState('time');
  const [sortDir,        setSortDir]        = useState('desc');

  useEffect(() => {
    const q = query(collection(db, 'signals'), orderBy('timestamp', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setSignals(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.action !== 'CLOSE' && (s.tradeId || s.status === 'duplicate_skipped')));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  useEffect(() => {
    fetch(HL_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ type: 'userFills', user: HL_WALLET }),
    })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setAllFills(data); })
      .catch(() => {});
  }, []);

  const strategies = useMemo(() => {
    const set = new Set(signals.map(s => s.strategy || s.source).filter(Boolean));
    return ['ALL', ...Array.from(set).sort()];
  }, [signals]);

  const symbols = useMemo(() => {
    const set = new Set(signals.map(s => (s.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '')).filter(Boolean));
    return ['ALL', ...Array.from(set).sort()];
  }, [signals]);

  const openSignalIds = useMemo(() => {
    const latestBySymbol = {};
    signals.forEach(s => {
      if (s.status !== 'executed') return;
      const ms = tsMs(s);
      if (!latestBySymbol[s.symbol] || ms > latestBySymbol[s.symbol].ms) {
        latestBySymbol[s.symbol] = { id: s.id, ms };
      }
    });
    const result = new Set();
    Object.values(latestBySymbol).forEach(({ id, ms }) => {
      const sig = signals.find(s => s.id === id);
      if (!sig) return;
      const coin     = (sig.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
      const dirLabel = (sig.direction || '').toUpperCase() === 'LONG' ? 'Long' : 'Short';
      const hasClosed = allFills.some(f =>
        f.coin === coin &&
        (f.dir || '').includes(dirLabel) &&
        (f.dir || '').includes('Close') &&
        f.time >= ms
      );
      if (!hasClosed) result.add(id);
    });
    return result;
  }, [signals, allFills]);

  const nextMsMap = useMemo(() => {
    const map = {};
    signals.forEach(sig => {
      const sigMs = tsMs(sig);
      const later = signals.filter(s => s.symbol === sig.symbol && s.id !== sig.id && tsMs(s) > sigMs && s.status !== 'duplicate_skipped');
      map[sig.id] = later.length > 0 ? tsMs(later[later.length - 1]) : null;
    });
    return map;
  }, [signals]);

  function computePnl(signal) {
    const result = tradeResult(signal, allFills, nextMsMap[signal.id]);
    return result ? result.net : null;
  }

  const sorted = useMemo(() => {
    const filtered = signals.filter(s => {
      if (sideFilter !== 'ALL' && (s.direction || '').toUpperCase() !== sideFilter) return false;
      const coin = (s.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
      if (symbolFilter !== 'ALL' && coin !== symbolFilter) return false;
      if (strategyFilter !== 'ALL' && (s.strategy || s.source) !== strategyFilter) return false;
      if (dateFrom || dateTo) {
        const ts = s.timestamp?.toDate ? s.timestamp.toDate() : s.timestamp ? new Date(s.timestamp) : null;
        if (!ts) return false;
        if (dateFrom && ts < new Date(dateFrom)) return false;
        if (dateTo  && ts > new Date(dateTo + 'T23:59:59')) return false;
      }
      return true;
    });

    const withPnl = filtered.map(s => ({ ...s, _pnl: computePnl(s) }));
    const keyFn   = SORT_KEYS[sortCol] || SORT_KEYS.time;
    return [...withPnl].sort((a, b) => {
      const av = keyFn(a), bv = keyFn(b);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signals, allFills, sideFilter, symbolFilter, strategyFilter, dateFrom, dateTo, sortCol, sortDir]);

  function clearFilters() {
    setSideFilter('ALL');
    setSymbolFilter('ALL');
    setStrategyFilter('ALL');
    setDateFrom('');
    setDateTo('');
  }

  function toggleExpand(id) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleSort(col) {
    setSortCol(prev => {
      if (prev === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return col; }
      setSortDir('asc');
      return col;
    });
  }

  function downloadCSV() {
    const headers = ['Status', 'Side', 'Symbol', 'Strategy', 'Entry', 'SL', 'TP', 'Size', 'Est PnL', 'Time'];
    const rows = sorted.map(s => {
      const coin = (s.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
      const ts   = s.timestamp?.toDate ? s.timestamp.toDate().toISOString()
                 : s.timestamp ? new Date(s.timestamp).toISOString() : '';
      const pnl  = s._pnl != null ? `${s._pnl > 0 ? '+' : ''}${s._pnl.toFixed(4)}` : '';
      return [
        s.status || '', s.direction || '',
        coin, s.strategy || s.source || '',
        s.fill_price || s.price || '', s.sl_price || '', s.tp_price || '',
        s.size != null ? `$${s.size}` : '', pnl, ts,
      ];
    });
    const csv  = [headers, ...rows].map(r => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `spinningcat-alerts-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function SortTh({ col, children }) {
    const active = sortCol === col;
    return (
      <th className={`al-th-sort${active ? ' al-th-sort--active' : ''}`} onClick={() => handleSort(col)}>
        {children}
        <span className="al-sort-arrow">{active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}</span>
      </th>
    );
  }

  const hasFilter = sideFilter !== 'ALL' || symbolFilter !== 'ALL' || strategyFilter !== 'ALL' || dateFrom || dateTo;

  return (
    <div>
      <div className="page-header">
        <h1>Alerts</h1>
        <p>Webhook signals received from TradingView · live</p>
      </div>

      <div className="al-toolbar card">
        <div className="al-filter-group">
          <label className="al-filter-label">Side</label>
          <div className="al-side-btns">
            {['ALL', 'LONG', 'SHORT'].map(side => (
              <button key={side}
                className={`al-side-btn al-side-btn--${side.toLowerCase()}${sideFilter === side ? ' active' : ''}`}
                onClick={() => setSideFilter(side)}>
                {side}
              </button>
            ))}
          </div>
        </div>

        <div className="al-filter-group">
          <label className="al-filter-label">Symbol</label>
          <select className="al-filter-input" value={symbolFilter} onChange={e => setSymbolFilter(e.target.value)}>
            {symbols.map(s => (
              <option key={s} value={s}>{s === 'ALL' ? 'All Symbols' : s}</option>
            ))}
          </select>
        </div>

        <div className="al-filter-group">
          <label className="al-filter-label">Strategy</label>
          <select className="al-filter-input" value={strategyFilter} onChange={e => setStrategyFilter(e.target.value)}>
            {strategies.map(s => (
              <option key={s} value={s}>{s === 'ALL' ? 'All Strategies' : s}</option>
            ))}
          </select>
        </div>

        <div className="al-filter-group">
          <label className="al-filter-label">From</label>
          <input type="date" className="al-filter-input" value={dateFrom}
            max={dateTo || toDateInput(new Date())} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div className="al-filter-group">
          <label className="al-filter-label">To</label>
          <input type="date" className="al-filter-input" value={dateTo}
            min={dateFrom} max={toDateInput(new Date())} onChange={e => setDateTo(e.target.value)} />
        </div>

        <div className="al-toolbar-right">
          {hasFilter && <button className="al-clear-btn" onClick={clearFilters}>Clear</button>}
          <button className="al-csv-btn" onClick={downloadCSV} disabled={sorted.length === 0}>↓ CSV</button>
          <span className="al-count">{sorted.length} alert{sorted.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, overflowX: 'auto' }}>
        {loading && (
          <div className="loading-state"><div className="spinner" /><p>Loading alerts…</p></div>
        )}

        {!loading && sorted.length === 0 && (
          <div className="empty-state">
            <span className="icon">🔔</span>
            <p>{hasFilter ? 'No alerts match your filters.' : 'No alerts yet — waiting for TradingView webhooks.'}</p>
          </div>
        )}

        {!loading && sorted.length > 0 && (
          <table className="al-table">
            <thead>
              <tr>
                <SortTh col="status">Status</SortTh>
                <SortTh col="side">Side</SortTh>
                <SortTh col="symbol">Symbol</SortTh>
                <SortTh col="strategy">Strategy</SortTh>
                <SortTh col="entry">Entry</SortTh>
                <SortTh col="sl">SL</SortTh>
                <SortTh col="tp">TP</SortTh>
                <SortTh col="size">Size</SortTh>
                <SortTh col="pnl">PnL</SortTh>
                <SortTh col="time">Time</SortTh>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(signal => {
                const coin       = (signal.symbol || '').replace(/USDT$/, '').replace(/PERP$/, '');
                const isLong     = (signal.direction || '').toUpperCase() !== 'SHORT';
                const { label, cls } = statusInfo(signal);
                const open       = expandedIds.has(signal.id);
                const isOpenPos  = openSignalIds.has(signal.id);
                const pnl        = signal._pnl;
                const nextSignalMs = nextMsMap[signal.id] ?? null;
                return [
                  <tr
                    key={signal.id}
                    className={[
                      open ? 'row--expanded' : '',
                      isOpenPos ? 'row--open-position' : '',
                    ].filter(Boolean).join(' ')}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleExpand(signal.id)}
                  >
                    <td><span className={`al-status-badge al-status-badge--${cls}`}>{label}</span></td>
                    <td>
                      <span className={`al-dir-badge al-dir-badge--${isLong ? 'long' : 'short'}`}>
                        {isLong ? 'LONG' : 'SHORT'}
                      </span>
                    </td>
                    <td>
                      <div className="al-coin">
                        <CoinIcon base={coin} size={18} />
                        <span className="al-coin-name">{coin || signal.symbol || '—'}</span>
                      </div>
                    </td>
                    <td className="strategy-cell">{signal.strategy || signal.source || '—'}</td>
                    <td>{formatPrice(signal.fill_price || signal.price)}</td>
                    <td className="al-red">{formatPrice(signal.sl_price)}</td>
                    <td className="al-green">{formatPrice(signal.tp_price)}</td>
                    <td>{signal.size != null ? `$${signal.size}` : '—'}</td>
                    <td className={pnl != null ? (pnl >= 0 ? 'pnl-positive' : 'pnl-negative') : 'pnl-open'}>
                      {pnl != null
                        ? `${pnl >= 0 ? '+' : '−'}$${Math.abs(pnl).toFixed(2)}`
                        : isOpenPos ? 'OPEN' : '—'}
                    </td>
                    <td className="al-time">{formatDateTime(signal.timestamp)}</td>
                    <td className="td-expand-col">{open ? '▲' : '▼'}</td>
                  </tr>,
                  open && (
                    <tr key={`${signal.id}-detail`} className="row--detail">
                      <td colSpan="12">
                        <TradeDetailPanel signal={signal} nextSignalMs={nextSignalMs} />
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Alerts;
